<template>
  <div>
    <v-card class="alert-salary-gov" outlined elevation="3">
      <v-card-title class="gradient-header white--text">
        <v-icon color="white" class="mr-2">mdi-cash-multiple</v-icon>
        <div class="title-content">
          <div class="main-title">ตรวจสอบค่าตอบแทนพนักงานราชการ</div>
          <div class="sub-title">
            งบกลาง รายการเงินสำรองจ่ายเพื่อกรณีฉุกเฉินหรือจำเป็น ปีงบประมาณ พ.ศ.
            2568
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="white"
          outlined
          class="ml-2"
          @click="exportToExcel"
          :disabled="!salaryData || salaryData.length === 0"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Excel
        </v-btn>
        <v-btn
          color="white"
          outlined
          class="ml-2"
          @click="exportToPDF"
          :disabled="!salaryData || salaryData.length === 0"
        >
          <v-icon left>mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>
        <v-btn
          color="white"
          outlined
          class="ml-2"
          @click="refreshData"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          รีเฟรช
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Loading State -->
        <v-skeleton-loader
          v-if="loading"
          type="table"
        ></v-skeleton-loader>

        <!-- Empty State -->
        <v-alert
          v-else-if="!salaryData || salaryData.length === 0"
          type="info"
          border="left"
          colored-border
          elevation="2"
          class="mb-0"
        >
          <div class="d-flex align-center">
            <v-icon left color="info" size="32">mdi-information</v-icon>
            <div>
              <div class="text-h6">ไม่พบข้อมูล</div>
              <div class="text-body-2">
                ไม่มีข้อมูลค่าตอบแทนพนักงานราชการในระบบ
              </div>
            </div>
          </div>
        </v-alert>

        <!-- Data Display -->
        <div v-else>
          <!-- Summary Card -->
          <v-card class="mb-4" outlined color="info lighten-5">
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="3">
                  <div class="text-center">
                    <v-icon color="info" size="40">mdi-account-group</v-icon>
                    <div class="text-h4 font-weight-bold info--text">
                      {{ salaryData.length }}
                    </div>
                    <div class="text-body-2 grey--text">จำนวนบุคลากร</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="3">
                  <div class="text-center">
                    <v-icon color="success" size="40">mdi-cash</v-icon>
                    <div class="text-h4 font-weight-bold success--text">
                      {{ totalSalary2567 | numberFormat }}
                    </div>
                    <div class="text-body-2 grey--text">
                      รวมเงินเดือน ปี 2567
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="3">
                  <div class="text-center">
                    <v-icon color="warning" size="40">mdi-cash-check</v-icon>
                    <div class="text-h4 font-weight-bold warning--text">
                      {{ totalSalary2568 | numberFormat }}
                    </div>
                    <div class="text-body-2 grey--text">
                      รวมเงินเดือน ปี 2568
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" sm="3">
                  <div class="text-center">
                    <v-icon color="primary" size="40">mdi-calculator</v-icon>
                    <div class="text-h4 font-weight-bold primary--text">
                      {{ totalSalaryAll | numberFormat }}
                    </div>
                    <div class="text-body-2 grey--text">รวมทั้งหมด</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Data Table -->
          <v-card outlined>
            <v-data-table
              :headers="headers"
              :items="salaryData"
              :search="search"
              :items-per-page="15"
              :footer-props="{
                'items-per-page-options': [10, 15, 25, 50, 100, -1],
                'items-per-page-text': 'แสดงต่อหน้า:',
                'items-per-page-all-text': 'ทั้งหมด'
              }"
              class="elevation-0 salary-table"
              dense
            >
              <template v-slot:top>
                <v-toolbar flat color="grey lighten-4">
                  <v-icon left>mdi-table</v-icon>
                  <v-toolbar-title>รายชื่อพนักงานราชการ</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="ค้นหา"
                    single-line
                    hide-details
                    dense
                    outlined
                    class="search-field"
                  ></v-text-field>
                </v-toolbar>
              </template>

              <template v-slot:item._no="{ item }">
                <v-chip small color="info lighten-4" class="font-weight-bold">
                  {{ item._no }}
                </v-chip>
              </template>

              <template v-slot:item.fname="{ item }">
                <div class="d-flex align-center">
                  <span class="font-weight-medium">{{ item.fname }}</span>
                  <v-chip
                    v-if="item['comment_s']"
                    x-small
                    color="warning"
                    text-color="white"
                    class="ml-2"
                  >
                    <v-icon x-small left>mdi-alert</v-icon>
                    {{ item["comment_s"] }}
                  </v-chip>
                </div>
              </template>

              <template v-slot:item.summary_salary_2567="{ item }">
                <span class="success--text font-weight-bold">
                  {{ item["summary_salary_2567"] | cleanNumber | formatCurrency }}
                </span>
              </template>

              <template v-slot:item.summary_salary_2568="{ item }">
                <span class="warning--text font-weight-bold">
                  {{ item["summary_salary_2568"] | cleanNumber | formatCurrency }}
                </span>
              </template>

              <template v-slot:item.total_salary="{ item }">
                <span class="primary--text font-weight-bold text-h6">
                  {{ item["total_salary"] | cleanNumber | formatCurrency }}
                </span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn icon color="primary" small @click="viewDetail(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>

              <template v-slot:no-data>
                <v-alert type="info" text class="ma-4">
                  ไม่พบข้อมูล
                </v-alert>
              </template>

              <template v-slot:no-results>
                <v-alert type="warning" text class="ma-4">
                  ไม่พบข้อมูลที่ค้นหา "{{ search }}"
                </v-alert>
              </template>

              <template v-slot:footer.prepend>
                <div class="ml-4 mt-2 mb-2">
                  <v-chip color="success" outlined class="mr-2">
                    <v-icon small left>mdi-cash</v-icon>
                    รวม 2567: {{ totalSalary2567 | numberFormat }} บาท
                  </v-chip>
                  <v-chip color="warning" outlined class="mr-2">
                    <v-icon small left>mdi-cash-check</v-icon>
                    รวม 2568: {{ totalSalary2568 | numberFormat }} บาท
                  </v-chip>
                  <v-chip color="primary" outlined>
                    <v-icon small left>mdi-calculator</v-icon>
                    รวมทั้งหมด: {{ totalSalaryAll | numberFormat }} บาท
                  </v-chip>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </div>
      </v-card-text>
    </v-card>

    <!-- Detail Dialog -->
    <v-dialog v-model="dialog" max-width="800">
      <v-card v-if="selectedItem">
        <v-card-title class="gradient-header white--text">
          <v-icon left color="white">mdi-account-details</v-icon>
          รายละเอียดค่าตอบแทน
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12">
              <div class="text-h5 mb-2">{{ selectedItem.fname }}</div>
              <div class="text-body-1 grey--text">
                {{ selectedItem.college_name }}
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined color="success lighten-5">
                <v-card-title class="text-h6 success--text">
                  <v-icon left color="success">mdi-calendar-check</v-icon>
                  ปีงบประมาณ 2567
                </v-card-title>
                <v-card-text>
                  <div class="detail-row">
                    <span>เงินเดือน/เดือน:</span>
                    <span class="font-weight-bold"
                      >{{ selectedItem["salary_2567"] | cleanNumber }} บาท</span
                    >
                  </div>
                  <div class="detail-row">
                    <span>จำนวนเดือน:</span>
                    <span class="font-weight-bold"
                      >{{
                        selectedItem["salary_month_2567"] | cleanNumber
                      }}
                      เดือน</span
                    >
                  </div>
                  <div class="detail-row">
                    <span>รวมเงินเดือน:</span>
                    <span class="font-weight-bold"
                      >{{
                        selectedItem["salary_sum_2567"] | cleanNumber
                      }}
                      บาท</span
                    >
                  </div>
                  <div class="detail-row">
                    <span>คำสั่ง:</span>
                    <span class="font-weight-bold">{{
                      selectedItem["order_2567"] | cleanNumber
                    }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="detail-row">
                    <span class="text-h6">รวมทั้งหมด:</span>
                    <span class="text-h6 success--text font-weight-bold">
                      {{ selectedItem["summary_salary_2567"] | cleanNumber }}
                      บาท
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined color="warning lighten-5">
                <v-card-title class="text-h6 warning--text">
                  <v-icon left color="warning">mdi-calendar-star</v-icon>
                  ปีงบประมาณ 2568
                </v-card-title>
                <v-card-text>
                  <div class="mb-3">
                    <div class="text-subtitle-2 grey--text">งวดที่ 1</div>
                    <div class="detail-row">
                      <span>เงินเดือน/เดือน:</span>
                      <span class="font-weight-bold"
                        >{{
                          selectedItem["salary_2568_1"] | cleanNumber
                        }}
                        บาท</span
                      >
                    </div>
                    <div class="detail-row">
                      <span>จำนวนเดือน:</span>
                      <span class="font-weight-bold"
                        >{{
                          selectedItem["salary_month_2568_1"] | cleanNumber
                        }}
                        เดือน</span
                      >
                    </div>
                    <div class="detail-row">
                      <span>รวม:</span>
                      <span class="font-weight-bold"
                        >{{
                          selectedItem["salary_sum_2568_1"] | cleanNumber
                        }}
                        บาท</span
                      >
                    </div>
                  </div>

                  <v-divider></v-divider>

                  <div class="mt-3">
                    <div class="text-subtitle-2 grey--text">งวดที่ 2</div>
                    <div class="detail-row">
                      <span>เงินเดือน/เดือน:</span>
                      <span class="font-weight-bold"
                        >{{
                          selectedItem["salary_2568_2"] | cleanNumber
                        }}
                        บาท</span
                      >
                    </div>
                    <div class="detail-row">
                      <span>จำนวนเดือน:</span>
                      <span class="font-weight-bold"
                        >{{
                          selectedItem["salary_month_2568_2"] | cleanNumber
                        }}
                        เดือน</span
                      >
                    </div>
                    <div class="detail-row">
                      <span>รวม:</span>
                      <span class="font-weight-bold"
                        >{{
                          selectedItem["salary_sum_2568_2"] | cleanNumber
                        }}
                        บาท</span
                      >
                    </div>
                  </div>

                  <v-divider class="my-2"></v-divider>
                  <div class="detail-row">
                    <span class="text-h6">รวมทั้งหมด:</span>
                    <span class="text-h6 warning--text font-weight-bold">
                      {{ selectedItem["summary_salary_2568"] | cleanNumber }}
                      บาท
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12">
              <v-card outlined color="primary lighten-5">
                <v-card-text class="text-center">
                  <div class="text-subtitle-1 grey--text mb-2">
                    รวมค่าตอบแทนทั้งหมด
                  </div>
                  <div class="text-h4 primary--text font-weight-bold">
                    {{ selectedItem["total_salary"] | cleanNumber }} บาท
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="selectedItem['comment_s']">
              <v-alert type="warning" border="left" colored-border>
                <div class="font-weight-bold">หมายเหตุ:</div>
                {{ selectedItem["comment_s"] }}
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialog = false">ปิด</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "AlertSalaryGov2568",
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      users: {},
      salaryData: [],
      search: "",
      dialog: false,
      selectedItem: null,
      headers: [
        {
          text: "ลำดับ",
          value: "_no",
          align: "center",
          width: "80px",
          sortable: true
        },
        {
          text: "ชื่อ-นามสกุล",
          value: "fname",
          align: "start",
          width: "250px"
        },
        {
          text: "หน่วยงาน",
          value: "college_name",
          align: "start",
          width: "200px"
        },
        {
          text: "รวม ปี 2567 (บาท)",
          value: "summary_salary_2567",
          align: "end",
          width: "150px"
        },
        {
          text: "รวม ปี 2568 (บาท)",
          value: "summary_salary_2568",
          align: "end",
          width: "150px"
        },
        {
          text: "รวมทั้งหมด (บาท)",
          value: "total_salary",
          align: "end",
          width: "170px"
        },
        {
          text: "ดูรายละเอียด",
          value: "actions",
          align: "center",
          sortable: false,
          width: "120px"
        }
      ]
    };
  },
  computed: {
    totalSalary2567() {
      return this.salaryData.reduce((sum, item) => {
        const salary = this.parseNumber(item["summary_salary_2567"]);
        return sum + salary;
      }, 0);
    },
    totalSalary2568() {
      return this.salaryData.reduce((sum, item) => {
        const salary = this.parseNumber(item["summary_salary_2568"]);
        return sum + salary;
      }, 0);
    },
    totalSalaryAll() {
      return this.salaryData.reduce((sum, item) => {
        const salary = this.parseNumber(item["total_salary"]);
        return sum + salary;
      }, 0);
    }
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await this.userQuery();
        await this.salary_gov_query();
      } catch (error) {
        console.error("Error loading data:", error);
        this.$toast.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        this.loading = false;
      }
    },
    async userQuery() {
      let userSession = JSON.parse(localStorage.getItem("user")) || null;

      let result = await this.$http.post("user.php", {
        user_ID: userSession.user_ID,
        ApiKey: "HRvec2021"
      });
      this.users = result.data;
    },
    async salary_gov_query() {
      let user_role = "";
      let user_code = "";
      if (this.users.user_status == "A") {
        user_role = "admin";
        user_code = "";
      } else {
        user_role = "college";
        user_code = this.users.user_code;
      }
      let result = await this.$http.post("salary_gov_2568.php", {
        ApiKey: "HRvec2021",
        user_role: user_role,
        college_code: user_code
      });

      if (result.data.success) {
        this.salaryData = result.data.data;
      } else if (Array.isArray(result.data)) {
        this.salaryData = result.data;
      } else {
        this.salaryData = [];
      }
    },
    async refreshData() {
      await this.loadData();
      this.$toast.success("รีเฟรชข้อมูลสำเร็จ");
    },
    viewDetail(item) {
      this.selectedItem = item;
      this.dialog = true;
    },
    parseNumber(value) {
      if (!value || value === "-" || value.trim() === "") return 0;
      const cleaned = value
        .toString()
        .replace(/,/g, "")
        .trim();
      return parseFloat(cleaned) || 0;
    },
    exportToExcel() {
      try {
        import("xlsx")
          .then(XLSX => {
            const exportData = this.salaryData.map((item, index) => ({
              ลำดับ: index + 1,
              "ชื่อ-นามสกุล": item.fname,
              หน่วยงาน: item.college_name,
              "เงินเดือน/เดือน ปี 2567": this.parseNumber(item["salary_2567"]),
              "จำนวนเดือน ปี 2567": this.parseNumber(item["salary_month_2567"]),
              "รวมเงินเดือน ปี 2567": this.parseNumber(
                item["summary_salary_2567"]
              ),
              "เงินเดือน/เดือน งวด 1 ปี 2568": this.parseNumber(
                item["salary_2568_1"]
              ),
              "จำนวนเดือน งวด 1 ปี 2568": this.parseNumber(
                item["salary_month_2568_1"]
              ),
              "รวมงวด 1 ปี 2568": this.parseNumber(item["salary_sum_2568_1"]),
              "เงินเดือน/เดือน งวด 2 ปี 2568": this.parseNumber(
                item["salary_2568_2"]
              ),
              "จำนวนเดือน งวด 2 ปี 2568": this.parseNumber(
                item["salary_month_2568_2"]
              ),
              "รวมงวด 2 ปี 2568": this.parseNumber(item["salary_sum_2568_2"]),
              "รวมทั้งหมด ปี 2568": this.parseNumber(
                item["summary_salary_2568"]
              ),
              รวมค่าตอบแทนทั้งสิ้น: this.parseNumber(item["total_salary"]),
              หมายเหตุ: item["comment_s"] || "-"
            }));

            exportData.push({
              ลำดับ: "",
              "ชื่อ-นามสกุล": "รวมทั้งหมด",
              หน่วยงาน: "",
              "เงินเดือน/เดือน ปี 2567": "",
              "จำนวนเดือน ปี 2567": "",
              "รวมเงินเดือน ปี 2567": this.totalSalary2567,
              "เงินเดือน/เดือน งวด 1 ปี 2568": "",
              "จำนวนเดือน งวด 1 ปี 2568": "",
              "รวมงวด 1 ปี 2568": "",
              "เงินเดือน/เดือน งวด 2 ปี 2568": "",
              "จำนวนเดือน งวด 2 ปี 2568": "",
              "รวมงวด 2 ปี 2568": "",
              "รวมทั้งหมด ปี 2568": this.totalSalary2568,
              รวมค่าตอบแทนทั้งสิ้น: this.totalSalaryAll,
              หมายเหตุ: ""
            });

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "ค่าตอบแทนพนักงานราชการ");

            const colWidths = [
              { wch: 8 },
              { wch: 30 },
              { wch: 35 },
              { wch: 15 },
              { wch: 15 },
              { wch: 18 },
              { wch: 20 },
              { wch: 18 },
              { wch: 15 },
              { wch: 20 },
              { wch: 18 },
              { wch: 15 },
              { wch: 18 },
              { wch: 20 },
              { wch: 30 }
            ];
            ws["!cols"] = colWidths;

            const date = new Date();
            const filename = `ค่าตอบแทนพนักงานราชการ_${date.getFullYear()}-${(
              date.getMonth() + 1
            )
              .toString()
              .padStart(2, "0")}-${date
              .getDate()
              .toString()
              .padStart(2, "0")}.xlsx`;

            XLSX.writeFile(wb, filename);

            this.$toast.success("ส่งออกข้อมูล Excel สำเร็จ");
          })
          .catch(error => {
            console.error("Error loading XLSX library:", error);
            this.$toast.error("กรุณาติดตั้ง library xlsx ก่อนใช้งาน");
          });
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$toast.error("เกิดข้อผิดพลาดในการส่งออก Excel");
      }
    },
    exportToPDF() {
      try {
        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>รายงานค่าตอบแทนพนักงานราชการ</title>
            <style>
              @page {
                size: A4 landscape;
                margin: 15mm;
              }
              body {
                font-family: 'Sarabun', 'TH SarabunPSK', sans-serif;
                font-size: 12pt;
                line-height: 1.4;
              }
              .header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #333;
                padding-bottom: 10px;
              }
              .header h1 {
                margin: 5px 0;
                font-size: 18pt;
              }
              .header h2 {
                margin: 5px 0;
                font-size: 14pt;
                color: #666;
              }
              .summary {
                margin: 15px 0;
                padding: 10px;
                background: #f5f5f5;
                border-radius: 5px;
              }
              .summary-grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 15px;
              }
              .summary-item {
                text-align: center;
              }
              .summary-item .label {
                font-size: 10pt;
                color: #666;
              }
              .summary-item .value {
                font-size: 16pt;
                font-weight: bold;
                color: #333;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
                font-size: 10pt;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #d9777b;
                color: white;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .text-right {
                text-align: right;
              }
              .text-center {
                text-align: center;
              }
              .total-row {
                background-color: #fff4dc !important;
                font-weight: bold;
              }
              .footer {
                margin-top: 20px;
                text-align: right;
                font-size: 10pt;
                color: #666;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>รายงานค่าตอบแทนพนักงานราชการ</h1>
              <h2>งบกลาง รายการเงินสำรองจ่ายเพื่อกรณีฉุกเฉินหรือจำเป็น ปีงบประมาณ พ.ศ. 2568</h2>
            </div>

            <div class="summary">
              <div class="summary-grid">
                <div class="summary-item">
                  <div class="label">จำนวนบุคลากร</div>
                  <div class="value">${this.salaryData.length} คน</div>
                </div>
                <div class="summary-item">
                  <div class="label">รวมเงินเดือน ปี 2567</div>
                  <div class="value">${this.totalSalary2567.toLocaleString(
                    "th-TH",
                    { minimumFractionDigits: 2 }
                  )} บาท</div>
                </div>
                <div class="summary-item">
                  <div class="label">รวมเงินเดือน ปี 2568</div>
                  <div class="value">${this.totalSalary2568.toLocaleString(
                    "th-TH",
                    { minimumFractionDigits: 2 }
                  )} บาท</div>
                </div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th class="text-center" style="width: 5%;">ลำดับ</th>
                  <th style="width: 25%;">ชื่อ-นามสกุล</th>
                  <th style="width: 25%;">หน่วยงาน</th>
                  <th class="text-right" style="width: 12%;">เงินเดือน ปี 2567</th>
                  <th class="text-right" style="width: 12%;">เงินเดือน ปี 2568</th>
                  <th class="text-right" style="width: 13%;">รวมทั้งหมด</th>
                  <th class="text-center" style="width: 8%;">หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                ${this.salaryData
                  .map(
                    (item, index) => `
                  <tr>
                    <td class="text-center">${index + 1}</td>
                    <td>${item.fname}</td>
                    <td>${item.college_name}</td>
                    <td class="text-right">${this.parseNumber(
                      item["summary_salary_2567"]
                    ).toLocaleString("th-TH", {
                      minimumFractionDigits: 2
                    })}</td>
                    <td class="text-right">${this.parseNumber(
                      item["summary_salary_2568"]
                    ).toLocaleString("th-TH", {
                      minimumFractionDigits: 2
                    })}</td>
                    <td class="text-right">${this.parseNumber(
                      item["total_salary"]
                    ).toLocaleString("th-TH", {
                      minimumFractionDigits: 2
                    })}</td>
                    <td class="text-center">${item["comment_s"] || "-"}</td>
                  </tr>
                `
                  )
                  .join("")}
                <tr class="total-row">
                  <td colspan="3" class="text-center"><strong>รวมทั้งหมด</strong></td>
                  <td class="text-right"><strong>${this.totalSalary2567.toLocaleString(
                    "th-TH",
                    { minimumFractionDigits: 2 }
                  )}</strong></td>
                  <td class="text-right"><strong>${this.totalSalary2568.toLocaleString(
                    "th-TH",
                    { minimumFractionDigits: 2 }
                  )}</strong></td>
                  <td class="text-right"><strong>${this.totalSalaryAll.toLocaleString(
                    "th-TH",
                    { minimumFractionDigits: 2 }
                  )}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <div class="footer">
              พิมพ์วันที่: ${new Date().toLocaleDateString("th-TH", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              })}
            </div>
          </body>
          </html>
        `;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(printContent);
        printWindow.document.close();

        printWindow.onload = function() {
          printWindow.focus();
          printWindow.print();
        };

        this.$toast.success("กำลังเตรียมไฟล์ PDF...");
      } catch (error) {
        console.error("Error exporting to PDF:", error);
        this.$toast.error("เกิดข้อผิดพลาดในการส่งออก PDF");
      }
    }
  },
  filters: {
    dateTH(val) {
      if (!val) return "";
      const d = new Date(val);
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear() + 543}`;
    },
    cleanNumber(val) {
      if (!val || val === "-" || val.toString().trim() === "") return "-";
      return val
        .toString()
        .replace(/,/g, "")
        .trim();
    },
    numberFormat(val) {
      if (!val || isNaN(val)) return "0";
      return parseFloat(val).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },
    formatCurrency(val) {
      if (!val || val === "-") return "-";
      const num = parseFloat(val);
      if (isNaN(num)) return "-";
      return num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style scoped>
/* Main Card Container */
.alert-salary-gov {
  margin: 16px 0;
}

/* Header Styling */
.gradient-header {
  background: linear-gradient(135deg, #263238 0%, #263238 100%);
  padding: 16px 24px !important;
}

.title-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.main-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
}

.sub-title {
  font-size: 0.875rem;
  opacity: 0.95;
  margin-top: 4px;
  line-height: 1.3;
}

/* Search Field Styling */
.search-field {
  max-width: 350px;
  background-color: white;
  border-radius: 4px;
}

.search-field >>> .v-input__control {
  min-height: 40px !important;
}

.search-field >>> .v-text-field__details {
  display: none;
}

/* Data Table Styling */
.salary-table {
  font-size: 0.95rem;
}

.salary-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.salary-table >>> thead th {
  background-color: #f5f5f5 !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  color: #424242 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  white-space: nowrap;
}

.salary-table >>> tbody td {
  padding: 12px 16px !important;
  font-size: 0.9rem !important;
  border-bottom: 1px solid #f0f0f0 !important;
}

.salary-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.salary-table >>> .v-data-footer {
  border-top: 2px solid #e0e0e0;
  padding: 8px 16px;
}

.salary-table >>> .v-data-footer__select .v-select {
  margin: 0 8px 0 0;
}

/* Summary Cards */
.v-card.blue.lighten-5 {
  border: 1px solid #f5f5f5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.v-card.blue.lighten-5 .v-card__text {
  padding: 20px;
}

/* Detail Row Styling */
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  min-height: 36px;
}

.detail-row:not(:last-child) {
  border-bottom: 1px solid #e0e0e0;
}

.detail-row span:first-child {
  color: #666;
  font-size: 0.95rem;
}

.detail-row span:last-child {
  text-align: right;
  font-size: 0.95rem;
}

/* Chip Styling */
.v-chip {
  height: 28px !important;
  font-size: 0.85rem !important;
  margin: 2px 4px;
}

.v-chip.v-chip--outlined {
  background-color: white !important;
  border-width: 1.5px !important;
}

.v-chip .v-icon {
  font-size: 16px !important;
}

/* Dialog Styling */
.v-dialog .v-card {
  border-radius: 8px;
  overflow: hidden;
}

.v-dialog .gradient-header {
  padding: 20px 24px !important;
}

.v-dialog .v-card__text {
  max-height: 70vh;
  overflow-y: auto;
}

/* Detail Cards in Dialog */
.v-dialog .v-card.outlined {
  border: 2px solid;
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.v-dialog .v-card.outlined:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.v-dialog .v-card.green.lighten-5 {
  border-color: #81c784;
}

.v-dialog .v-card.orange.lighten-5 {
  border-color: #e6b04d;
}

.v-dialog .v-card.primary.lighten-5 {
  border-color: #90a4ae;
}

/* Summary Section Styling */
.text-center .v-icon {
  margin-bottom: 8px;
}

.text-h4 {
  margin: 8px 0;
  line-height: 1.2;
}

.text-body-2.grey--text {
  font-size: 0.875rem;
  margin-top: 4px;
}

/* Empty State and Alert Styling */
.v-alert {
  border-radius: 8px;
}

.v-alert .v-icon {
  margin-right: 12px;
}

.v-alert.info {
  background-color: #f5f5f5 !important;
  border-left: 4px solid #455a64 !important;
}

.v-alert.warning {
  background-color: #fff4dc !important;
  border-left: 4px solid #a86400 !important;
}

/* Button Styling */
.v-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
}

.v-btn.white--text {
  font-weight: 600 !important;
}

.v-btn .v-icon {
  font-size: 20px;
}

/* Loading State */
.v-skeleton-loader {
  border-radius: 8px;
}

/* Divider Styling */
.v-divider {
  margin: 0 !important;
}

/* Card Actions */
.v-card__actions {
  padding: 16px 24px;
}

/* Toolbar Styling */
.v-toolbar {
  border-bottom: 1px solid #e0e0e0;
}

.v-toolbar-title {
  font-size: 1.1rem !important;
  font-weight: 600 !important;
}

/* Footer Chips */
.v-data-footer .v-chip {
  margin: 4px;
  font-size: 0.85rem !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .gradient-header {
    flex-wrap: wrap;
  }

  .title-content {
    width: 100%;
    margin-bottom: 12px;
  }

  .main-title {
    font-size: 1.1rem;
  }

  .sub-title {
    font-size: 0.8rem;
  }

  .search-field {
    max-width: 100%;
    margin-top: 8px;
  }

  .v-btn {
    margin: 4px 2px !important;
    min-width: auto !important;
    padding: 0 12px !important;
  }

  .v-btn .v-btn__content {
    font-size: 0.85rem;
  }

  .salary-table >>> thead th {
    font-size: 0.8rem !important;
    padding: 8px 4px !important;
  }

  .salary-table >>> tbody td {
    font-size: 0.85rem !important;
    padding: 8px 4px !important;
  }
}

@media (max-width: 600px) {
  .alert-salary-gov {
    margin: 8px 0;
  }

  .v-card__text {
    padding: 12px !important;
  }

  .v-chip {
    font-size: 0.75rem !important;
    height: 24px !important;
    margin: 2px;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-row span:last-child {
    text-align: left;
  }

  .text-h4 {
    font-size: 1.5rem !important;
  }

  .v-dialog .v-card__text {
    padding: 16px !important;
  }

  .salary-table >>> .v-data-table__wrapper {
    overflow-x: scroll;
  }

  .v-data-footer .v-chip {
    font-size: 0.75rem !important;
  }
}

/* Print Styles */
@media print {
  .gradient-header .v-btn,
  .v-data-table__wrapper .actions,
  .search-field {
    display: none !important;
  }

  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }

  .salary-table >>> tbody tr {
    page-break-inside: avoid;
  }
}

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.v-card {
  animation: fadeIn 0.3s ease-out;
}

/* Focus States */
.v-btn:focus {
  outline: 2px solid #d9777b;
  outline-offset: 2px;
}

.v-text-field:focus-within {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

/* Card Hover Effects */
.v-card.outlined:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  transition: box-shadow 0.3s ease;
}

/* Icon Colors */
.v-icon.mdi-cash-multiple {
  font-size: 28px;
}

.blue--text {
  color: #455a64 !important;
}

.green--text {
  color: #2e7d32 !important;
}

.orange--text {
  color: #a86400 !important;
}

.purple--text {
  color: #b3151b !important;
}

.grey--text {
  color: #757575 !important;
}

/* Improved Text Readability */
.font-weight-bold {
  font-weight: 600 !important;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.text-subtitle-2 {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
}

/* Enhanced Loading State */
.v-skeleton-loader >>> .v-skeleton-loader__bone {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Data Table Dense Mode */
.salary-table.v-data-table--dense >>> tbody td {
  height: 40px !important;
}

/* Alternating Row Colors */
.salary-table >>> tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}

/* Number Formatting */
.salary-table >>> tbody td[class*="text-right"] {
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
}
</style>